import produce from 'immer';

import {RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';

export type FileType = 'csv' | 'sdf';

export type ModalStep = 'CONFIGURE' | 'FILE_UPLOAD' | 'MAP_COLUMNS' | 'SUBMITTING' | 'DONE';

export interface RegistrationConfig {
  registryId: string;
  namingStrategy: RegistryLabelingStrategy;
  folderId: string;
}

export interface ModalState {
  step: ModalStep;
  initialFolderId: string | null;
  schemaId: string | null;
  compoundSchemaId: string | null;
  registrationConfig: RegistrationConfig | null;
  file: File | null;
  s3Url: string | null;
  headers: Array<string> | null;
  columnMappings: Record<string, string>;
  fieldExpansion: Record<string, Array<string>>;
}

export const INITIAL_STATE: ModalState = {
  step: 'CONFIGURE',
  initialFolderId: null,
  schemaId: null,
  compoundSchemaId: null,
  registrationConfig: null,
  file: null,
  s3Url: null,
  headers: null,
  columnMappings: {},
  fieldExpansion: {},
};

interface SetSchemaAction {
  type: 'SET_SCHEMA';
  payload: {
    schemaId: string | null;
    compoundSchemaId: string | null;
  };
}

interface SetRegistryIdAction {
  type: 'SET_REGISTRY_ID';
  payload: {registryId: string | null};
}

interface SetNamingStrategyAction {
  type: 'SET_NAMING_STRATEGY';
  payload: {namingStrategy: RegistryLabelingStrategy};
}

interface SetFolderIdAction {
  type: 'SET_FOLDER_ID';
  payload: {folderId: string | null};
}

interface SetFileUploadResultAction {
  type: 'SET_FILE_UPLOAD_RESULT';
  payload: {
    file: File;
    s3Url: string;
    headers: Array<string>;
  };
}

interface ClearFileUploadAction {
  type: 'CLEAR_FILE_UPLOAD';
}

interface SetColumnMappingsAction {
  type: 'SET_COLUMN_MAPPINGS';
  payload: {
    columnMappings: Record<string, string>;
    fieldExpansion: Record<string, Array<string>>;
  };
}

interface GoToStepAction {
  type: 'GO_TO_STEP';
  payload: {
    step: ModalStep;
  };
}

interface ResetAction {
  type: 'RESET';
}

export type ModalAction =
  | SetSchemaAction
  | SetRegistryIdAction
  | SetNamingStrategyAction
  | SetFolderIdAction
  | SetFileUploadResultAction
  | ClearFileUploadAction
  | SetColumnMappingsAction
  | GoToStepAction
  | ResetAction;

export default function moleculeImportReducer(state: ModalState, action: ModalAction): ModalState {
  switch (action.type) {
    case 'SET_SCHEMA':
      return produce(state, (draft) => {
        draft.schemaId = action.payload.schemaId;
        draft.compoundSchemaId = action.payload.compoundSchemaId;
      });
    case 'SET_REGISTRY_ID':
      return produce(state, (draft) => {
        draft.registrationConfig = {
          ...(draft.registrationConfig ?? {
            registryId: '',
            namingStrategy: RegistryLabelingStrategy.NEW_IDS,
            folderId: draft.initialFolderId ?? '',
          }),
          registryId: action.payload.registryId ?? '',
        };
      });
    case 'SET_NAMING_STRATEGY':
      return produce(state, (draft) => {
        draft.registrationConfig = {
          ...(draft.registrationConfig ?? {
            registryId: '',
            namingStrategy: RegistryLabelingStrategy.NEW_IDS,
            folderId: draft.initialFolderId ?? '',
          }),
          namingStrategy: action.payload.namingStrategy,
        };
      });
    case 'SET_FOLDER_ID':
      return produce(state, (draft) => {
        draft.registrationConfig = {
          ...(draft.registrationConfig ?? {
            registryId: '',
            namingStrategy: RegistryLabelingStrategy.NEW_IDS,
            folderId: draft.initialFolderId ?? '',
          }),
          folderId: action.payload.folderId ?? '',
        };
      });
    case 'SET_FILE_UPLOAD_RESULT':
      return produce(state, (draft) => {
        draft.file = action.payload.file;
        draft.s3Url = action.payload.s3Url;
        draft.headers = action.payload.headers;
      });
    case 'CLEAR_FILE_UPLOAD':
      return produce(state, (draft) => {
        draft.file = null;
        draft.s3Url = null;
        draft.headers = null;
      });
    case 'SET_COLUMN_MAPPINGS':
      return produce(state, (draft) => {
        draft.columnMappings = action.payload.columnMappings;
        draft.fieldExpansion = action.payload.fieldExpansion;
      });
    case 'GO_TO_STEP': {
      const stepOrder: Array<ModalStep> = ['CONFIGURE', 'FILE_UPLOAD', 'MAP_COLUMNS', 'SUBMITTING', 'DONE'];
      const targetIndex = stepOrder.indexOf(action.payload.step);
      const currentIndex = stepOrder.indexOf(state.step);
      return produce(state, (draft) => {
        draft.step = action.payload.step;
        if (targetIndex < currentIndex) {
          const fileUploadIndex = stepOrder.indexOf('FILE_UPLOAD');
          if (targetIndex < fileUploadIndex) {
            draft.file = null;
            draft.s3Url = null;
            draft.headers = null;
          }
          const mapColumnsIndex = stepOrder.indexOf('MAP_COLUMNS');
          if (targetIndex < mapColumnsIndex) {
            draft.columnMappings = {};
            draft.fieldExpansion = {};
          }
        }
      });
    }
    case 'RESET':
      return INITIAL_STATE;
    default:
      return state;
  }
}
